//
// Copyright 2024 DXOS.org
//
import * as Schema from 'effect/Schema';
import { BaseError } from '@dxos/errors';
import { invariant } from '@dxos/invariant';
import { SpaceId } from '@dxos/keys';
/**
 * HTTP header sent on every Edge request to classify traffic for metering.
 */
export const EDGE_CLIENT_TAG_HEADER = 'X-DXOS-Client-Tag';
/**
 * HTTP header sent on every Edge request to provide a BYOK (Bring Your Own Key) for AI services.
 */
export const BYOK_HEADER = 'X-BYOK';
// TODO(burdon): Rename EdgerRouterEndpoint.
// If we would rename it, we need to be careful to not break composer production.
export var EdgeService;
(function (EdgeService) {
    EdgeService["SUBDUCTION_REPLICATOR"] = "subduction-replicator";
    /**
     * Control feed replicator (hypercore append only logs) for the space.
     */
    // TODO(mykola): Remove once we migrate to keyhive for access control.
    EdgeService["FEED_REPLICATOR"] = "feed-replicator";
    /**
     * Feed replicator (ordered data list) for the space.
     */
    // TODO(mykola): Rename to FEED_REPLICATOR when we migrate to keyhive for access control.
    EdgeService["QUEUE_REPLICATOR"] = "queue-replicator";
    EdgeService["SWARM"] = "swarm";
    EdgeService["SIGNAL"] = "signal";
    EdgeService["STATUS"] = "status";
    /**
     * User-to-user notices held for an identity until acknowledged (e.g., space invitation notices).
     */
    EdgeService["INBOX"] = "inbox";
})(EdgeService || (EdgeService = {}));
const _SerializedError = Schema.Struct({
    name: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
    context: Schema.optional(Schema.Record(Schema.String, Schema.Any)),
    stack: Schema.optional(Schema.String),
    cause: Schema.optional(Schema.suspend(() => SerializedError)),
});
export const SerializedError = _SerializedError;
/**
 * Use this to create a response from the Edge service.
 */
export const EdgeResponse = Object.freeze({
    success: (data, status = 200) => {
        invariant(status >= 200 && status < 300, 'Status code must be in the 2xx range');
        const headers = new Headers({ 'Content-Type': 'application/json' });
        return new Response(JSON.stringify({
            success: true,
            data,
        }), {
            status,
            headers,
        });
    },
    failure: ({ message, error, errorEncoded, data, shouldRetryAfter, status = 500, }) => {
        const headers = new Headers({ 'Content-Type': 'application/json' });
        if (shouldRetryAfter) {
            headers.set('Retry-After', String(shouldRetryAfter));
        }
        return new Response(JSON.stringify({
            success: false,
            message,
            data,
            error: error ? ErrorCodec.encode(error) : errorEncoded,
        }), {
            status,
            headers,
        });
    },
});
/**
 * Note: Do not change the values of these enums, this values are stored in the FunctionVersions database.
 */
export const FunctionRuntimeKind = Schema.Enum({
    // https://developers.cloudflare.com/cloudflare-for-platforms/workers-for-platforms/
    WORKERS_FOR_PLATFORMS: 'WORKERS_FOR_PLATFORMS',
    // https://developers.cloudflare.com/workers/runtime-apis/bindings/worker-loader/
    WORKER_LOADER: 'WORKER_LOADER',
});
export var EdgeAgentStatus;
(function (EdgeAgentStatus) {
    EdgeAgentStatus["ACTIVE"] = "active";
    EdgeAgentStatus["INACTIVE"] = "inactive";
    EdgeAgentStatus["NOT_FOUND"] = "not_found";
})(EdgeAgentStatus || (EdgeAgentStatus = {}));
export var OAuthProvider;
(function (OAuthProvider) {
    OAuthProvider["ATLASSIAN"] = "atlassian";
    OAuthProvider["ATPROTO"] = "atproto";
    OAuthProvider["CLOUDFLARE"] = "cloudflare";
    OAuthProvider["DISCORD"] = "discord";
    OAuthProvider["GITHUB"] = "github";
    OAuthProvider["GOOGLE"] = "google";
    OAuthProvider["LINEAR"] = "linear";
    OAuthProvider["NOTION"] = "notion";
    OAuthProvider["SLACK"] = "slack";
    OAuthProvider["TRELLO"] = "trello";
})(OAuthProvider || (OAuthProvider = {}));
/** atproto OAuth scopes for the Atmosphere integration and account-recovery flows. */
export const ATPROTO_OAUTH_SCOPES = ['atproto', 'transition:generic', 'transition:email'];
/**
 * `AccessToken.source` for the Atmosphere connection — the atproto account bound to a DXOS identity.
 * atproto accounts are portable (both PDS and handle can change), so this is not a hostname. Every
 * subsystem that looks up the account's credential matches on it, hence its home beside
 * {@link OAuthProvider}. The connector that operates the connection is identified separately, by
 * {@link OAuthProvider.ATPROTO} as its `Connector.id`.
 */
export const ATMOSPHERE_SOURCE = 'atproto.local';
export const InitiateOAuthFlowRequestSchema = Schema.Struct({
    provider: Schema.Enum(OAuthProvider),
    spaceId: Schema.String.pipe(Schema.refine(SpaceId.isValid)), // TODO(burdon): Use SpaceId.
    accessTokenId: Schema.String,
    scopes: Schema.mutable(Schema.Array(Schema.String)),
    // Set to true if we don't want periodic token refreshes in background, for cases like account connect
    noRefresh: Schema.optional(Schema.Boolean),
    // Provider-specific (user handle or did for atproto) hint for auth server resolution
    loginHint: Schema.optional(Schema.String),
    // Return a 302 redirect to composer://oauth/callback instead of HTML.
    // Required for ASWebAuthenticationSession (iOS) which blocks JavaScript redirects.
    nativeAppRedirect: Schema.optional(Schema.Boolean),
    // OAuth-based account recovery: when purpose === 'register', kms-service writes a
    // recovery binding for `identityKey` after the OAuth flow completes; when 'recovery',
    // kms-service mints a one-time `recoveryProof` the client forwards to db-service.
    registerRecovery: Schema.optional(Schema.Boolean),
    identityKey: Schema.optional(Schema.String),
    purpose: Schema.optional(Schema.Literals(['register', 'recovery'])),
});
/**
 * Stand-in that EDGE returns (and clients persist on the `AccessToken` object) in place of a real
 * token for providers whose credentials EDGE custodies. The token itself is never replicated into
 * a space; holders of this value fetch the live one from `/oauth/token` per use.
 */
export const MANAGED_ACCESS_TOKEN = 'dxos:managed-access-token';
/** Whether a stored credential is a {@link MANAGED_ACCESS_TOKEN} placeholder rather than a usable token. */
export const isManagedAccessToken = (token) => token === MANAGED_ACCESS_TOKEN;
export var EdgeWebsocketProtocol;
(function (EdgeWebsocketProtocol) {
    EdgeWebsocketProtocol["V0"] = "edge-ws-v0";
    /**
     * Enables message framing and muxing by service-id.
     */
    EdgeWebsocketProtocol["V1"] = "edge-ws-v1";
})(EdgeWebsocketProtocol || (EdgeWebsocketProtocol = {}));
/**
 * Prefix of the `Sec-WebSocket-Protocol` entry carrying the client's SDK version (e.g. `dxos-version.0.12.0`).
 * A subprotocol entry because browsers cannot set headers on a WebSocket; the router never selects it.
 */
export const EDGE_CLIENT_VERSION_PROTOCOL_PREFIX = 'dxos-version.';
/** HTTP header carrying the client's SDK version, the {@link EDGE_CLIENT_VERSION_PROTOCOL_PREFIX} of a plain request. */
export const EDGE_CLIENT_VERSION_HEADER = 'X-DXOS-Version';
/** `failure.data.type` of EDGE's 426 refusing an SDK older than the oldest it serves. */
export const EDGE_CLIENT_TOO_OLD = 'client_too_old';
const MAX_ERROR_DEPTH = 3;
/**
 * Codec for serializing and deserializing Edge Errors.
 */
export const ErrorCodec = Object.freeze({
    encode: (err, depth = 0) => ({
        name: 'name' in err ? err.name : err.code || 'Error',
        message: err.message,
        stack: err.stack,
        cause: err.cause instanceof Error && depth < MAX_ERROR_DEPTH ? ErrorCodec.encode(err.cause, depth + 1) : undefined,
    }),
    decode: (serializedError, depth = 0) => {
        let err;
        if (typeof serializedError.name === 'string') {
            err = new BaseError(serializedError.name, {
                message: serializedError.message ?? 'Unknown error',
                cause: serializedError.cause && depth < MAX_ERROR_DEPTH
                    ? ErrorCodec.decode(serializedError.cause, depth + 1)
                    : undefined,
                context: serializedError.context,
            });
            if (serializedError.stack) {
                Object.defineProperty(err, 'stack', {
                    value: serializedError.stack,
                });
            }
        }
        else {
            err = new Error(serializedError.message ?? 'Unknown error', {
                cause: serializedError.cause && depth < MAX_ERROR_DEPTH
                    ? ErrorCodec.decode(serializedError.cause, depth + 1)
                    : undefined,
            });
            if (serializedError.stack) {
                Object.defineProperty(err, 'stack', {
                    value: serializedError.stack,
                });
            }
        }
        return err;
    },
});
/**
 * Codec for serializing and deserializing Edge unhandled HTTP errors.
 */
export const EdgeHttpErrorCodec = Object.freeze({
    encode: (err) => new Response(JSON.stringify({
        error: ErrorCodec.encode(err),
    }), { status: 500 }),
    decode: async (response) => {
        if (response.headers.get('Content-Type') !== 'application/json') {
            const body = await response.clone().text();
            return new Error(body.slice(0, 256));
        }
        const body = await response.clone().json();
        if (!('error' in body)) {
            return undefined;
        }
        return ErrorCodec.decode(body.error);
    },
});
/** Authentication scheme naming a DXOS verifiable presentation. */
export const VERIFIABLE_PRESENTATION_SCHEME = 'VerifiablePresentation';
/** Marks the credential payload as a base64-encoded protobuf. */
const VERIFIABLE_PRESENTATION_TOKEN_PREFIX = 'pb;base64,';
/**
 * Subprotocol prefix carrying credentials on the WebSocket upgrade, where there is no
 * `Authorization` header to put them in.
 */
export const WS_AUTH_PROTOCOL_PREFIX = 'base64url.bearer.authorization.dxos.org';
/**
 * Codec for the credentials header — the single definition of how an encoded verifiable
 * presentation is framed on the wire, in both places it travels:
 *
 * - `Authorization: VerifiablePresentation pb;base64,<base64>` for HTTP.
 * - `base64url.bearer.authorization.dxos.org.<token>` as a WebSocket subprotocol, which admits a
 *   narrower alphabet: `=` padding is stripped and `/` is substituted with `|`, neither being
 *   legal in a subprotocol token.
 *
 * This lives in protocols because both ends own half of it — the client encodes, the Edge worker
 * decodes — and the two had drifted into separate copies of the same string literals.
 *
 * Decoding is deliberately lenient where the RFCs are: the auth-scheme is matched
 * case-insensitively (RFC 9110 §11.1) and anything unrecognised yields `undefined` rather than
 * throwing, so a caller can fall through to another auth method instead of failing the request.
 */
export const EdgeCredentialsHeaderCodec = Object.freeze({
    /** Encode a presentation as an `Authorization` header value. */
    encode: (presentation) => `${VERIFIABLE_PRESENTATION_SCHEME} ${VERIFIABLE_PRESENTATION_TOKEN_PREFIX}${uint8ArrayToBase64(presentation)}`,
    /** Decode an `Authorization` header value, or `undefined` if it carries no presentation. */
    decode: (header) => {
        if (!header) {
            return undefined;
        }
        const separator = header.indexOf(' ');
        if (separator === -1) {
            return undefined;
        }
        if (header.slice(0, separator).toLowerCase() !== VERIFIABLE_PRESENTATION_SCHEME.toLowerCase()) {
            return undefined;
        }
        const token = header.slice(separator + 1);
        if (!token.startsWith(VERIFIABLE_PRESENTATION_TOKEN_PREFIX)) {
            return undefined;
        }
        return base64ToUint8Array(token.slice(VERIFIABLE_PRESENTATION_TOKEN_PREFIX.length));
    },
    /** Encode a presentation as a WebSocket subprotocol token. */
    encodeWebSocketProtocol: (presentation) => {
        const token = uint8ArrayToBase64(presentation).replace(/=*$/, '').replaceAll('/', '|');
        return `${WS_AUTH_PROTOCOL_PREFIX}.${token}`;
    },
    /** Decode a WebSocket subprotocol token, or `undefined` if it is not a credentials token. */
    decodeWebSocketProtocol: (protocol) => {
        if (!protocol?.startsWith(`${WS_AUTH_PROTOCOL_PREFIX}.`)) {
            return undefined;
        }
        const token = protocol.slice(WS_AUTH_PROTOCOL_PREFIX.length + 1).replaceAll('|', '/');
        return base64ToUint8Array(token);
    },
});
// `Buffer` is unavailable in the browser bundle, so go through the platform base64 primitives.
const uint8ArrayToBase64 = (bytes) => {
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
};
const base64ToUint8Array = (value) => {
    // `atob` rejects the stripped padding the WebSocket framing produces; restore it first.
    const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
    // `atob` throws on characters outside the base64 alphabet, which would break the `undefined`
    // contract both decoders document for anything unrecognised.
    let binary;
    try {
        binary = atob(padded);
    }
    catch {
        return undefined;
    }
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
};
//
// Account / Invitation
//
export const INVITATION_CODE_LENGTH = 8;
export const DEFAULT_INVITATIONS_PER_ACCOUNT = 5;
/** Crockford base32 alphabet (no I, L, O, U). Case-insensitive on the wire. */
export const INVITATION_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/**
 * A vanity code is an admin-chosen prefix, a dash, and random characters from
 * {@link INVITATION_CODE_ALPHABET}: `SF-MEETUP-7K2Q`. Hub-service matches codes ignoring case and
 * dashes, so `sfmeetup7k2q` redeems it too.
 */
export const VANITY_PREFIX_MIN_LENGTH = 4;
export const VANITY_PREFIX_MAX_LENGTH = 20;
export const VANITY_SUFFIX_LENGTH = 4;
export const VANITY_CODE_MAX_LENGTH = VANITY_PREFIX_MAX_LENGTH + 1 + VANITY_SUFFIX_LENGTH;
/** 4-20 characters, at least 4 of them letters or digits, with single dashes between them. */
export const VANITY_PREFIX_PATTERN = new RegExp(`^(?=.{${VANITY_PREFIX_MIN_LENGTH},${VANITY_PREFIX_MAX_LENGTH}}$)(?=(?:-?[A-Za-z0-9]){${VANITY_PREFIX_MIN_LENGTH}})[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$`, 'u');
export const MAX_CODES_PER_REQUEST = 1000;
export const MAX_REDEMPTIONS_PER_CODE = 100;
/**
 * A code as a user types it: generated or vanity, any case, optionally hyphenated. Hub-service
 * normalizes before lookup. Every issued code has at least {@link INVITATION_CODE_LENGTH} characters.
 */
export const InvitationCodeSchema = Schema.String.pipe(Schema.check(Schema.isPattern(new RegExp(`^[A-Za-z0-9-]{${INVITATION_CODE_LENGTH},${2 * VANITY_CODE_MAX_LENGTH}}$`, 'u'))));
export const CheckEmailExistsRequestSchema = Schema.Struct({
    email: Schema.String,
});
export const ValidateInvitationCodeRequestSchema = Schema.Struct({
    code: InvitationCodeSchema,
});
/**
 * Body of `POST /account/login`. Existing-account email recovery only -- account creation redeems
 * an invitation code via `/account/invitation-code/redeem` instead, so this never creates
 * identities or waitlist rows.
 */
export const LoginRequestSchema = Schema.Struct({
    email: Schema.String,
    identityDid: Schema.optional(Schema.String),
    identityKey: Schema.optional(Schema.String),
    /**
     * Where the emailed link hands the token back, defaulting to the hub's configured app URL. The
     * hub honors only origins it owns, so a CLI can name its local callback server here.
     */
    redirectUrl: Schema.optional(Schema.String),
});
/**
 * Response from `POST /account/login`. The shape is identical regardless of
 * whether the email is registered, so the endpoint is safe against enumeration.
 * Regular emails are delivered out-of-band and the response is `{}`; a recovery
 * token is never returned inline. Test emails in dev-like environments
 * short-circuit with `needsIdentity` / `admitted` before any token exists.
 */
export const LoginResponseSchema = Schema.Struct({
    needsIdentity: Schema.optional(Schema.Boolean),
    admitted: Schema.optional(Schema.Boolean),
});
// Two-step signup: invitation code + identity DID + email.
export const RedeemInvitationCodeRequestSchema = Schema.Struct({
    code: Schema.optional(InvitationCodeSchema),
    identityDid: Schema.optional(Schema.String),
    /** Raw hex public key, stored alongside the DID for the magic-link recovery flow. */
    identityKey: Schema.optional(Schema.String),
    email: Schema.String,
});
export const GetAccountResponseSchema = Schema.Struct({
    identityDid: Schema.String,
    email: Schema.String,
    emailVerified: Schema.Boolean,
    /** ISO timestamp. */
    createdAt: Schema.String,
    invitationsRemaining: Schema.Number,
});
export const AccountInvitationSchema = Schema.Struct({
    code: Schema.String,
    /** ISO timestamp. */
    createdAt: Schema.String,
    redeemedByIdentityDid: Schema.optional(Schema.String),
    /** ISO timestamp. */
    redeemedAt: Schema.optional(Schema.String),
});
export const ListAccountInvitationsResponseSchema = Schema.Struct({
    invitations: Schema.Array(AccountInvitationSchema),
});
/**
 * Submitted by users without an Account who want to request access. Captured
 * by Hub for admin follow-up (e.g. mailing list, Discord notification).
 */
export const RequestAccessRequestSchema = Schema.Struct({
    email: Schema.String,
    /** Optional: identity DID the user is currently signed in as. */
    identityDid: Schema.optional(Schema.String),
    /** Optional free-form message from the requester. */
    message: Schema.optional(Schema.String),
});
//
// Metering (VP auth)
//
/** Structured usage key aligned with {@link MeteringLimitSchema} (without cap/window fields). */
export const MeteringUsageKeySchema = Schema.Struct({
    /** Event type (e.g. `ai`). */
    eventType: Schema.String,
    /** Value key being metered (e.g. `outputTokens`). */
    valueKey: Schema.String,
    /**
     * Positional match against the event's subtype segments (e.g. model); `*` matches any
     * segment, and positions beyond the pattern are unconstrained.
     */
    subtypePattern: Schema.Array(Schema.String),
});
/** Rolling-window usage total for a structured key. */
export const MeteringUsageItemSchema = Schema.Struct({
    ...MeteringUsageKeySchema.fields,
    amount: Schema.Number,
});
/** A single raw usage bucket (1h resolution) for a structured key. */
export const MeteringUsageBucketSchema = Schema.Struct({
    ...MeteringUsageKeySchema.fields,
    /** Bucket start timestamp (epoch ms, floored to the hour). */
    bucketStart: Schema.Number,
    amount: Schema.Number,
});
export const MeteringLimitSchema = Schema.Struct({
    /** Event type the limit applies to (e.g. `ai`). */
    eventType: Schema.String,
    /** Value key being limited (e.g. `outputTokens`). */
    valueKey: Schema.String,
    /**
     * Positional match against the event's subtype segments (e.g. model); `*` matches any
     * segment, and positions beyond the pattern are unconstrained.
     */
    subtypePattern: Schema.Array(Schema.String),
    /** Rolling-window cap; `null` means unlimited. */
    limit: Schema.NullOr(Schema.Number),
    /** Window duration in seconds. */
    windowDuration: Schema.Number,
});
export const GetProfileUsageResponseSchema = Schema.Struct({
    profileId: Schema.String,
    usage: Schema.Array(MeteringUsageItemSchema),
    limits: Schema.Array(MeteringLimitSchema),
    buckets: Schema.Array(MeteringUsageBucketSchema),
});
export const AdminGrantInvitationsRequestSchema = Schema.Struct({
    identityDid: Schema.String,
    count: Schema.Number,
});
export const AdminCreateInvitationCodesRequestSchema = Schema.Struct({
    count: Schema.Number.check(Schema.isInt(), Schema.isBetween({ minimum: 1, maximum: MAX_CODES_PER_REQUEST })),
    note: Schema.optional(Schema.String),
    /** Plan the redeemer inherits, overriding the default user plan. Single-use codes only. */
    planName: Schema.optional(Schema.String),
    /** How many accounts may sign up with each code (default 1), e.g. for an event code. */
    maxRedemptions: Schema.optional(Schema.Number.check(Schema.isInt(), Schema.isBetween({ minimum: 1, maximum: MAX_REDEMPTIONS_PER_CODE }))),
    /**
     * Vanity prefix (requires `count: 1`). The code is the uppercased prefix, a dash, and
     * {@link VANITY_SUFFIX_LENGTH} random characters, so `sf-meetup` yields e.g. `SF-MEETUP-7K2Q`.
     */
    prefix: Schema.optional(Schema.String.check(Schema.isPattern(VANITY_PREFIX_PATTERN))),
}).check(Schema.makeFilter((request) => {
    const issues = [];
    if (request.prefix !== undefined && request.count !== 1) {
        issues.push({ path: ['count'], issue: 'A vanity code is created one at a time; count must be 1.' });
    }
    if (request.planName && (request.maxRedemptions ?? 1) > 1) {
        issues.push({ path: ['planName'], issue: 'A code that carries a plan must be single-use.' });
    }
    return issues;
}));
export const AdminRevokeInvitationCodeRequestSchema = Schema.Struct({
    code: InvitationCodeSchema,
});
/**
 * Account/invitation-related variants placed in `EdgeFailure.data.type`.
 * EdgeErrorData is open-ended; these are documentation for known values.
 */
export const ACCOUNT_ERROR_TYPES = [
    'invitation_code_invalid',
    'invitation_code_already_redeemed',
    'invitation_code_revoked',
    'email_already_registered',
    'identity_already_associated',
    'no_invitations_remaining',
    'identity_not_associated_with_account',
    'no_account',
    'rate_limited',
];
//# sourceMappingURL=edge.js.map